// A deferred store purchase (iOS Ask to Buy, a Google Play pending payment)
// is remembered per user so no paywall surface starts a second one while the
// store finishes it — across remounts and app restarts. The in-memory guards
// in use-revenuecat-purchase.js never see a pending purchase.
const STORAGE_KEY = 'bosko:pendingStorePurchase';
// Apple expires an unanswered Ask to Buy request after 24 hours; Play keeps
// cash and bank-transfer payments open for up to about three days.
const PENDING_TTL_MS = 72 * 60 * 60 * 1000;

const listeners = new Set();
// The account whose deferred purchase this app session already announced, so
// a second surface confirming the same approval stays quiet.
let claimedUserId = null;

function notify() {
  listeners.forEach((listener) => listener());
}

function readEntry() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function removeEntry() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export function markPendingPurchase(userId) {
  if (userId == null) return;
  claimedUserId = null;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        userId: String(userId),
        expiresAt: Date.now() + PENDING_TTL_MS,
      }),
    );
  } catch {
    // Storage unavailable: the hook's in-memory state still blocks this screen.
  }
  notify();
}

// Read during render, so an expired entry is dropped without notifying.
export function hasPendingPurchase(userId) {
  if (userId == null) return false;
  const entry = readEntry();
  if (!entry) return false;
  if (!(entry.expiresAt > Date.now())) {
    removeEntry();
    return false;
  }
  return entry.userId === String(userId);
}

export function clearPendingPurchase() {
  removeEntry();
  notify();
}

// Called once the server confirms the purchase. Consumes the remembered flag
// and returns whether the caller should announce it (toast + new chat): false
// only when another surface — the app-wide watcher or a paywall's Check
// status — already did. With nothing remembered (storage unavailable, or the
// flag expired) the caller is the only one who can.
export function claimPendingPurchase(userId) {
  if (userId == null) return false;
  if (hasPendingPurchase(userId)) {
    claimedUserId = String(userId);
    clearPendingPurchase();
    return true;
  }
  return claimedUserId !== String(userId);
}

// Notified when a purchase is deferred, claimed or cleared.
export function subscribePendingPurchase(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
